import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from '../hooks/use-toast';
import { PartePreviewDialog } from '../components/PartePreviewDialog';
import {
  CheckCircle2, RefreshCw, FileText, ClipboardList, ShieldCheck, Receipt,
  Eye, ArrowRight, Inbox, Loader2,
} from 'lucide-react';

const fmtFecha = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const TYPE_META = {
  oc_vb:       { label: 'OC sin VB',          icon: FileText,     color: 'text-amber-700',   bg: 'bg-amber-50 dark:bg-amber-950/30' },
  requisicion: { label: 'Requisiciones',      icon: ClipboardList, color: 'text-blue-700',    bg: 'bg-blue-50 dark:bg-blue-950/30' },
  parte_vb:    { label: 'Partes para VB',      icon: ShieldCheck,   color: 'text-emerald-700', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
  proforma:    { label: 'Proformas',           icon: Receipt,       color: 'text-purple-700',  bg: 'bg-purple-50 dark:bg-purple-950/30' },
};

export default function BandejaAprobaciones() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], counts: { oc_vb: 0, requisicion: 0, parte_vb: 0, proforma: 0, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [confirmDialog, setConfirmDialog] = useState(null); // {item}
  const [busyIds, setBusyIds] = useState({});
  const [previewParteId, setPreviewParteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/aprobaciones/mis-pendientes');
      setData(res.data);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'No se pudo cargar la bandeja', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => {
    if (filter === 'all') return data.items;
    return data.items.filter(it => it.tipo === filter);
  }, [data.items, filter]);

  const ejecutarAprobacion = async (item) => {
    setBusyIds(prev => ({ ...prev, [item.id + item.tipo]: true }));
    try {
      const accion = item.accion_aprobar;
      const config = { method: accion.metodo, url: accion.url };
      if (accion.body) config.data = accion.body;
      await api.request(config);
      toast({ title: '✅ Aprobado', description: item.titulo });
      // Refresh
      load();
    } catch (err) {
      toast({
        title: 'Error al aprobar',
        description: err.response?.data?.detail || err.message || '',
        variant: 'destructive',
      });
    } finally {
      setBusyIds(prev => {
        const n = { ...prev };
        delete n[item.id + item.tipo];
        return n;
      });
      setConfirmDialog(null);
    }
  };

  const onAprobar = (item) => {
    if (item.accion_aprobar?.metodo === 'REDIRECT') {
      navigate(item.accion_aprobar.url);
      return;
    }
    setConfirmDialog({ item });
  };

  const onVerDetalle = (item) => {
    // Iter 321 — para partes diarios, abrir previsualizador en vez de navegar
    if (item.tipo === 'parte_vb') {
      setPreviewParteId(item.id);
      return;
    }
    if (item.url_detalle) navigate(item.url_detalle);
  };

  return (
    <div className="container mx-auto p-3 sm:p-6 max-w-4xl space-y-4" data-testid="page-aprobaciones">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Inbox className="h-6 w-6 sm:h-7 sm:w-7 text-emerald-600" />
            Bandeja de Aprobaciones
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Todo lo que está esperando tu aprobación, en un solo lugar.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="gap-1.5" data-testid="btn-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Resumen counters mobile-friendly */}
      <Card>
        <CardContent className="p-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`p-2 rounded text-left transition border ${filter === 'all' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40' : 'border-transparent hover:bg-muted'}`}
            data-testid="filter-all"
          >
            <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Total</p>
            <p className="text-2xl font-bold tabular-nums text-emerald-700">{data.counts.total}</p>
          </button>
          {Object.entries(TYPE_META).map(([key, meta]) => {
            const Icon = meta.icon;
            const count = data.counts[key] || 0;
            const isActive = filter === key;
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                disabled={count === 0}
                className={`p-2 rounded text-left transition border disabled:opacity-50 ${isActive ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40' : 'border-transparent hover:bg-muted'}`}
                data-testid={`filter-${key}`}
              >
                <p className={`text-[10px] uppercase tracking-wider flex items-center gap-1 ${meta.color}`}>
                  <Icon className="h-3 w-3" /> {meta.label}
                </p>
                <p className="text-2xl font-bold tabular-nums">{count}</p>
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando…
        </div>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
            <p className="font-medium">¡Todo al día!</p>
            <p className="text-xs mt-1">No hay nada pendiente de tu aprobación.</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={filter} onValueChange={setFilter} className="w-full">
          {/* Tabs hidden on mobile (use top counter buttons instead); shown on desktop */}
          <TabsList className="hidden sm:grid grid-cols-5">
            <TabsTrigger value="all">Todos ({data.counts.total})</TabsTrigger>
            {Object.entries(TYPE_META).map(([key, meta]) => (
              <TabsTrigger key={key} value={key} disabled={(data.counts[key] || 0) === 0}>
                {meta.label} ({data.counts[key] || 0})
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={filter} className="space-y-2 mt-3">
            {filteredItems.map((item) => {
              const meta = TYPE_META[item.tipo] || {};
              const Icon = meta.icon || FileText;
              const busy = !!busyIds[item.id + item.tipo];
              const isPin = item.accion_aprobar?.metodo === 'REDIRECT';
              return (
                <Card key={`${item.tipo}-${item.id}`} className={`${meta.bg} border-l-4 ${meta.color?.replace('text-', 'border-')}`} data-testid={`card-${item.tipo}-${item.id}`}>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`h-4 w-4 ${meta.color}`} />
                          <Badge variant="outline" className="text-[10px] uppercase">{meta.label}</Badge>
                          {item.extra?.desbloqueada && (
                            <Badge className="text-[10px] bg-orange-100 text-orange-800 border-orange-300">🔓 Desbloqueada</Badge>
                          )}
                          {isPin && (
                            <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300">PIN</Badge>
                          )}
                        </div>
                        <p className="font-bold text-base sm:text-lg leading-tight">{item.titulo}</p>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 truncate">{item.subtitulo}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{fmtFecha(item.fecha)}{item.creador ? ` · por ${item.creador}` : ''}</p>
                      </div>
                      <div className="flex sm:flex-col gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onVerDetalle(item)}
                          className="gap-1 flex-1 sm:flex-none h-9"
                          data-testid={`btn-ver-${item.tipo}-${item.id}`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Ver</span>
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => onAprobar(item)}
                          disabled={busy}
                          className="gap-1 flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 h-9 min-w-[100px]"
                          data-testid={`btn-aprobar-${item.tipo}-${item.id}`}
                        >
                          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isPin ? <ArrowRight className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                          {isPin ? 'Firmar' : 'Aprobar'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
        </Tabs>
      )}

      {/* Preview de parte diario (Iter 321) */}
      <PartePreviewDialog
        parteId={previewParteId}
        open={!!previewParteId}
        onOpenChange={(o) => !o && setPreviewParteId(null)}
      />

      {/* Confirm dialog */}
      <AlertDialog open={!!confirmDialog} onOpenChange={(o) => !o && setConfirmDialog(null)}>
        <AlertDialogContent data-testid="dialog-confirmar-aprobacion">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar aprobación</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>¿Confirmás aprobar <b>{confirmDialog?.item.titulo}</b>?</p>
                <p className="text-xs text-muted-foreground">{confirmDialog?.item.subtitulo}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-aprobacion">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => ejecutarAprobacion(confirmDialog.item)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-confirmar-aprobacion"
            >
              Sí, aprobar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
